import { TZDate } from '@date-fns/tz';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiAsyncError } from 'noddi-types';
import {
  addDays,
  compareDates,
  CustomDateFormats,
  getStartOfWeek,
  isSameDay,
  NoddiDateFormats,
  noddiFormatDate,
  parseDate
} from 'noddi-util';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  findSlotIdForTime,
  getDateRange,
  getWeekDates,
  type CapacitySlotLabelMode,
  type HeldSlotWindow,
  type NormalizedCapacitySlot
} from './capacityUtils';
import type { DayWithSlots } from './types';
import { useStationaryCapacityQuery } from './useStationaryCapacityQuery';

type RollingRangeConfig = {
  kind: 'rolling-days';
  dayCount: number;
  labelMode?: CapacitySlotLabelMode;
};

type WeekRangeConfig = {
  kind: 'week';
  maxDaysAhead?: number;
  labelMode?: CapacitySlotLabelMode;
  /** Prefetch prev/next week capacity while viewing the current week. */
  prefetchNeighbors?: boolean;
  /** Keep the initial empty week hidden while advancing to the next week. */
  skipInitialEmptyWeek?: boolean;
};

export type StationaryCapacityRangeConfig = RollingRangeConfig | WeekRangeConfig;

export type UseStationaryCapacityRangeInput = {
  garageId?: number;
  salesItemIds?: number[];
  serviceSeconds?: number;
  applyBookingLeadTime?: boolean;
  /** Pin slots to one bay. Omit to auto-assign. */
  workstationId?: number | null;
  detailed?: boolean;
  enabled?: boolean;
  range: StationaryCapacityRangeConfig;
  /** Sell surfaces: keep free starts fresh while the picker is open. See the query hook. */
  keepFresh?: boolean;
  /** This customer's own hold, kept visible after a refetch drops it from free starts. */
  heldWindow?: HeldSlotWindow | null;
  /**
   * Week mode only. Opens on the week that contains this `yyyy-MM-dd` date.
   * A date before today is ignored. A date past the booking horizon clamps to it.
   */
  initialDate?: string | null;
};

export type UseStationaryCapacityRangeReturn = {
  days: DayWithSlots[];
  slots: NormalizedCapacitySlot[];
  slotLookup: Map<string, NormalizedCapacitySlot>;
  resolveSlotId: (selected: { startsAt: string; endsAt: string } | null) => string | null;
  goToPreviousRange: () => void;
  goToNextRange: () => void;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  dateRangeLabel: string;
  isPending: boolean;
  error: NoddiAsyncError | null;
  timeZone: string | undefined;
  /** Every slot's length: the booking's total service time. Null until loaded, and on the legacy live EonTyre path. */
  serviceSeconds: number | null;
};

/**
 * Shared stationary capacity navigation over {@link useStationaryCapacityQuery}.
 * - rolling-days: partner-style fixed window (e.g. 14 days)
 * - week: customer Mon–Sat week with optional max-days-ahead + neighbor prefetch
 */
export function useStationaryCapacityRange({
  garageId,
  salesItemIds,
  serviceSeconds,
  applyBookingLeadTime,
  workstationId,
  detailed = false,
  enabled,
  range,
  keepFresh = false,
  heldWindow,
  initialDate
}: UseStationaryCapacityRangeInput): UseStationaryCapacityRangeReturn {
  const [mountTime] = useState(() => Date.now());
  const [manualCursor, setManualCursor] = useState<Date | null>(null);
  const [hasCheckedInitialWeek, setHasCheckedInitialWeek] = useState(false);

  const salesItemIdsKey = salesItemIds?.join(',') ?? '';
  const capacityQueryKey = `${garageId ?? ''}|${salesItemIdsKey}|${serviceSeconds ?? ''}|${range.kind}|${initialDate ?? ''}`;
  const [prevCapacityQueryKey, setPrevCapacityQueryKey] = useState(capacityQueryKey);

  // Reset navigation cursor when capacity inputs change (React "adjust while rendering").
  if (capacityQueryKey !== prevCapacityQueryKey) {
    setPrevCapacityQueryKey(capacityQueryKey);
    setManualCursor(null);
    setHasCheckedInitialWeek(false);
  }

  const isEnabled = enabled ?? garageId != null;
  const provisionalToday = useMemo(() => new Date(mountTime), [mountTime]);
  const provisionalMaxDate = useMemo(() => {
    if (range.kind !== 'week' || range.maxDaysAhead == null) {
      return null;
    }
    return addDays(provisionalToday, range.maxDaysAhead) ?? null;
  }, [provisionalToday, range]);
  const initialCursor = useMemo(
    () => resolveInitialCursor({ initialDate, today: provisionalToday, maxDate: provisionalMaxDate, kind: range.kind }),
    [initialDate, provisionalToday, provisionalMaxDate, range.kind]
  );
  const currentCursor = manualCursor ?? initialCursor;

  const rangeDates = useMemo(() => {
    if (range.kind === 'week') {
      return getWeekDates(currentCursor);
    }
    return getDateRange(currentCursor, range.dayCount);
  }, [currentCursor, range]);

  const rangeDatesInWindow = useMemo(() => {
    const withinMax = provisionalMaxDate
      ? rangeDates.filter((date) => !compareDates(date, provisionalMaxDate, 'isAfter'))
      : rangeDates;
    if (range.kind !== 'week') {
      return withinMax;
    }
    // A week always starts on Monday, so the current week reaches into days that already passed.
    // Callers that skip booking lead time (partner) would otherwise get bookable slots in the past.
    return withinMax.filter(
      (date) => isSameDay(date, provisionalToday) || compareDates(date, provisionalToday, 'isAfter')
    );
  }, [rangeDates, provisionalMaxDate, provisionalToday, range.kind]);

  const dateFromApi = noddiFormatDate(rangeDatesInWindow[0] ?? rangeDates[0], NoddiDateFormats.API_DATE);
  const dateToApi = noddiFormatDate(
    rangeDatesInWindow[rangeDatesInWindow.length - 1] ?? rangeDates[rangeDates.length - 1],
    NoddiDateFormats.API_DATE
  );

  const labelMode: CapacitySlotLabelMode = range.labelMode ?? (range.kind === 'week' ? 'start-only' : 'start-end');

  const {
    days,
    slots,
    slotLookup,
    timeZone,
    serviceSeconds: slotServiceSeconds,
    isPending: isCapacityPending,
    error
  } = useStationaryCapacityQuery({
    garageId,
    dateFrom: dateFromApi,
    dateTo: dateToApi,
    salesItemIds,
    serviceSeconds,
    applyBookingLeadTime,
    workstationId,
    detailed,
    enabled: isEnabled,
    rangeDates: rangeDatesInWindow,
    labelMode,
    keepFresh,
    heldWindow
  });

  const toZoned = useCallback(
    (value: string | number | Date): Date => (timeZone ? new TZDate(new Date(value), timeZone) : new Date(value)),
    [timeZone]
  );

  const today = useMemo(() => toZoned(mountTime), [toZoned, mountTime]);
  const maxDate = useMemo(() => {
    if (range.kind !== 'week' || range.maxDaysAhead == null) {
      return null;
    }
    return addDays(today, range.maxDaysAhead);
  }, [today, range]);

  const stepDays = range.kind === 'week' ? 7 : range.dayCount;

  // Rolling: non-zoned mount clock (partner). Week: garage-zoned "today" once known (customer).
  const isPreviousDisabled =
    range.kind === 'rolling-days' ? currentCursor <= provisionalToday : compareDates(rangeDates[0], today, 'isBefore');

  const isNextDisabled =
    maxDate != null
      ? (() => {
          const nextCursor = addDays(currentCursor, stepDays);
          if (!nextCursor) {
            return true;
          }
          if (range.kind === 'week') {
            return compareDates(getStartOfWeek(nextCursor), maxDate, 'isAfter');
          }
          return compareDates(nextCursor, maxDate, 'isAfter');
        })()
      : false;

  const dateRangeLabel =
    range.kind === 'week'
      ? `${noddiFormatDate(rangeDatesInWindow[0] ?? rangeDates[0], CustomDateFormats.WEEKDAY_WITH_NUMBER_AND_MONTH)} - ${noddiFormatDate(
          rangeDatesInWindow[rangeDatesInWindow.length - 1] ?? rangeDates[rangeDates.length - 1],
          CustomDateFormats.WEEKDAY_WITH_NUMBER_AND_MONTH
        )}`
      : `${noddiFormatDate(rangeDates[0], NoddiDateFormats.DATE)} – ${noddiFormatDate(
          rangeDates[rangeDates.length - 1],
          NoddiDateFormats.DATE
        )}`;

  const navigate = (direction: 'prev' | 'next') => {
    if (direction === 'next' && isNextDisabled) {
      return;
    }
    if (direction === 'prev' && isPreviousDisabled) {
      return;
    }
    const next = addDays(currentCursor, direction === 'next' ? stepDays : -stepDays);
    if (next) {
      setManualCursor(next);
    }
  };

  const shouldResolveInitialWeek =
    range.kind === 'week' && !!range.skipInitialEmptyWeek && !hasCheckedInitialWeek && !isCapacityPending && !error;
  const shouldSkipInitialEmptyWeek = shouldResolveInitialWeek && days.length === 0 && !isNextDisabled;

  // Resolve before commit so customers never see the empty initial picker flash.
  if (shouldResolveInitialWeek) {
    setHasCheckedInitialWeek(true);
    if (shouldSkipInitialEmptyWeek) {
      setManualCursor(addDays(currentCursor, 7) ?? null);
    }
  }

  // Neighbor prefetch only for week mode when requested.
  const toRange = (dates: Date[] | null) =>
    dates
      ? {
          dateFrom: noddiFormatDate(dates[0], NoddiDateFormats.API_DATE),
          dateTo: noddiFormatDate(dates[dates.length - 1], NoddiDateFormats.API_DATE)
        }
      : null;

  const prevWeekRange =
    range.kind === 'week' && range.prefetchNeighbors && !isPreviousDisabled
      ? toRange(getWeekDates(addDays(currentCursor, -7)!))
      : null;
  const nextWeekRange =
    range.kind === 'week' && range.prefetchNeighbors && !isNextDisabled
      ? toRange(getWeekDates(addDays(currentCursor, 7)!))
      : null;

  useEffect(() => {
    if (range.kind !== 'week' || !range.prefetchNeighbors || !isEnabled || garageId == null) {
      return;
    }
    for (const neighbor of [prevWeekRange, nextWeekRange]) {
      if (neighbor) {
        void noddiAsync.prefetch({
          type: URLKeys.getStationaryCapacitySlots,
          input: {
            garageId,
            salesItemIds,
            dateFrom: neighbor.dateFrom,
            dateTo: neighbor.dateTo,
            detailed
          }
        });
      }
    }
    // Intentionally keyed on dateFrom strings (stable) rather than range object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefetch neighbors only
  }, [isEnabled, garageId, detailed, salesItemIdsKey, prevWeekRange?.dateFrom, nextWeekRange?.dateFrom, range.kind]);

  return {
    days,
    slots,
    slotLookup,
    resolveSlotId: (selected) => findSlotIdForTime(slots, selected),
    goToPreviousRange: () => navigate('prev'),
    goToNextRange: () => navigate('next'),
    isPreviousDisabled,
    isNextDisabled,
    dateRangeLabel,
    isPending: !isEnabled || isCapacityPending || shouldSkipInitialEmptyWeek,
    error,
    timeZone,
    serviceSeconds: slotServiceSeconds
  };
}

/** Week containing `initialDate`, never before today and never past the horizon. */
const resolveInitialCursor = ({
  initialDate,
  today,
  maxDate,
  kind
}: {
  initialDate?: string | null;
  today: Date;
  maxDate: Date | null;
  kind: StationaryCapacityRangeConfig['kind'];
}): Date => {
  if (kind !== 'week' || initialDate == null || initialDate === '') {
    return today;
  }

  const parsed = parseDate(initialDate.slice(0, 10), NoddiDateFormats.API_DATE);
  if (Number.isNaN(parsed.getTime())) {
    return today;
  }

  // Midnight of today is before "now", so compare the calendar day, not the instant.
  const isPast = !isSameDay(parsed, today) && compareDates(parsed, today, 'isBefore');
  if (isPast) {
    return today;
  }

  if (maxDate && !isSameDay(parsed, maxDate) && compareDates(parsed, maxDate, 'isAfter')) {
    return maxDate;
  }

  return parsed;
};
